import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Surface, PageSection, formatINR } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Search, ScrollText, Users, Building } from "lucide-react";
import { toast } from "sonner";

/** Ledger index — lists payers with running balances. Shared between Customer and Franchise variants. */
export default function LedgerIndex({ payerType }) {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [dueOnly, setDueOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = payerType === "customer" ? "/sales/customer-balances" : "/sales/franchise-balances";
      const r = await api.get(url);
      setRows(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, [payerType]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let out = rows;
    if (search) {
      const s = search.toLowerCase();
      out = out.filter((r) =>
        (r.name || "").toLowerCase().includes(s) ||
        (r.code || "").toLowerCase().includes(s) ||
        (r.mobile || "").toLowerCase().includes(s)
      );
    }
    if (dueOnly) out = out.filter((r) => Number(r.balance) > 0.005);
    return out;
  }, [rows, search, dueOnly]);

  const summary = useMemo(() => ({
    receivable: rows.filter((r) => r.balance > 0.005).reduce((s, r) => s + r.balance, 0),
    advance: rows.reduce((s, r) => s + Number(r.unallocated_advance || 0), 0),
    payerCount: rows.length,
    dueCount: rows.filter((r) => r.balance > 0.005).length,
  }), [rows]);

  const Icon = payerType === "customer" ? Users : Building;
  const openLedger = (r) => nav(payerType === "customer" ? `/sales/customers/${r.id}/ledger` : `/sales/franchises/${r.id}/ledger`);

  return (
    <div className="space-y-4">
      <PageSection
        title={payerType === "customer" ? "Customer Ledger" : "Franchise Ledger"}
        description={payerType === "customer"
          ? "Running balance across every customer. Positive balance = customer owes HQ; negative = customer has advance with HQ."
          : "Running balance across every franchise. Franchise-billed invoices roll up here."}
      >
        <div className="grid grid-cols-4 gap-3 mb-4">
          <SummaryCard label="Total Receivable" value={summary.receivable} tone="text-[#9B1C1C]" bold />
          <SummaryCard label="Total Advance" value={summary.advance} tone="text-amber-700" />
          <SummaryCard label={payerType === "customer" ? "Customers" : "Franchises"} value={summary.payerCount} raw />
          <SummaryCard label="With Dues" value={summary.dueCount} raw tone="text-red-700" />
        </div>
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-200 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 max-w-sm min-w-[220px]">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / code / mobile…" className="pl-8" data-testid="li-search" />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
              <input type="checkbox" checked={dueOnly} onChange={(e) => setDueOnly(e.target.checked)} className="accent-[#9B1C1C]" data-testid="li-due-only" />
              Show only with dues
            </label>
            <span className="ml-auto text-xs text-gray-500">{filtered.length} of {rows.length}</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2 text-right">Opening</th>
                <th className="px-3 py-2 text-right">Invoiced</th>
                <th className="px-3 py-2 text-right">Received</th>
                <th className="px-3 py-2 text-right">Advance</th>
                <th className="px-3 py-2 text-right">Balance</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400">Loading balances…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-10 text-center text-gray-400"><Icon className="h-8 w-8 mx-auto mb-2 opacity-30" /> No {payerType === "customer" ? "customers" : "franchises"} match.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openLedger(r)} data-testid={`li-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold">{r.code}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-[11px] text-gray-500">{payerType === "customer" ? `${r.source || ""} · ${r.customer_type || ""}` : (r.state || "")}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">{r.mobile || "—"}{r.gstin ? <div className="font-mono text-[10px]">{r.gstin}</div> : null}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-gray-500">{formatINR(r.opening_balance)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-gray-700">{formatINR(r.invoiced)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-emerald-700">{formatINR(r.received)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-amber-700">{r.unallocated_advance > 0.005 ? formatINR(r.unallocated_advance) : "—"}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-semibold ${r.balance > 0.005 ? "text-red-700" : r.balance < -0.005 ? "text-emerald-700" : "text-gray-500"}`}>{formatINR(r.balance)}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={(e) => { e.stopPropagation(); openLedger(r); }} className="h-7 w-7 rounded hover:bg-blue-50 inline-flex items-center justify-center" title="Open ledger" data-testid={`li-open-${r.id}`}>
                      <ScrollText className="h-3.5 w-3.5 text-blue-600" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
      </PageSection>
    </div>
  );
}

function SummaryCard({ label, value, tone = "", bold = false, raw = false }) {
  return (
    <div className="bg-white border border-gray-200 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`tabular-nums mt-0.5 ${tone} ${bold ? "font-heading font-bold text-xl" : "font-semibold text-lg"}`}>
        {raw ? Number(value || 0).toLocaleString() : formatINR(value || 0)}
      </div>
    </div>
  );
}
